import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {ColDef} from "ag-grid-community";
import {CustomCellRendererProps} from "ag-grid-react";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {format} from "date-fns";

type ReviewStatus = "monitoring" | "safe" | "api_sending_stopped" | "account_disabled";
type ReviewAction = "mark_safe" | "monitor" | "stop_api_sending" | "resume_api_sending" | "disable_account";

interface FlaggedAccount {
	user_id: number
	user_email: string
	status: ReviewStatus
	suspicious_count: number
	flags_since_review: number
	last_flagged_on: string | null
	categories: Record<string, number>
	domains: string[]
	workspaces: string[]
	rate_limit_hits_7d: number
	api_sending_disabled: boolean
	reviewed_by: string | null
	reviewed_on: string | null
	notes: string
}

interface AbuseSignal {
	id: number
	workspace_name: string
	email_id: string
	from: string
	to: string
	subject: string
	html_body: string | null
	plain_body: string | null
	category: string
	confidence: number
	reason: string
	flagged_on: string
}

interface ApiSettings {
	api_send_interval_seconds: number
	api_max_scheduled_per_domain: number
	api_abuse_alert_threshold: number
	api_abuse_confidence_threshold: number
	enable_api_abuse_alert_email: boolean
	api_bounce_limit_count: number
	api_bounce_window_minutes: number
	api_bounce_pause_minutes: number
	enable_api_bounce_pause_email: boolean
}

interface BouncePause {
	managed_subdomain_id: number
	domain: string
	user_email: string
	workspace_name: string
	bounce_count: number
	window_minutes: number
	paused_on: string
	paused_until: string | null
}

const STATUS_LABELS: Record<ReviewStatus, string> = {
	monitoring: "Monitoring",
	safe: "Safe",
	api_sending_stopped: "API Sending Stopped",
	account_disabled: "Account Disabled",
};

const STATUS_COLORS: Record<ReviewStatus, "warning" | "success" | "error" | "default"> = {
	monitoring: "warning",
	safe: "success",
	api_sending_stopped: "error",
	account_disabled: "error",
};

const ACTIONS: { action: ReviewAction, label: string, color: "success" | "warning" | "error" | "primary", confirm: string }[] = [
	{action: "mark_safe", label: "Mark as safe", color: "success",
		confirm: "The account keeps sending. Only new flags count towards the next alert."},
	{action: "monitor", label: "Continue monitoring", color: "primary",
		confirm: "Nothing changes for the account. It stays in the monitoring list."},
	{action: "stop_api_sending", label: "Stop API sending", color: "warning",
		confirm: "New API sends are rejected with 403 and the account's queued API emails are failed."},
	{action: "resume_api_sending", label: "Resume API sending", color: "primary",
		confirm: "The account can send through the API again."},
	{action: "disable_account", label: "Disable account", color: "error",
		confirm: "Stops API sending, revokes every API key, blocks campaign creation and cancels all running campaigns."},
];

function formatDate(value: string | null) {
	return value ? format(new Date(value), "dd MMM yyyy, hh:mm a") : "-";
}

function ApiSettingsCard() {
	const {enqueueSnackbar} = useSnackbar();
	const [settings, setSettings] = useState<ApiSettings>();

	const settingsQuery = useQuery({
		queryKey: ["adminApiSettings"],
		queryFn: () => authenticateAndFetchData("/admin/api-settings/"),
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (settingsQuery.data?.data) {
			setSettings(settingsQuery.data.data as ApiSettings);
		}
	}, [settingsQuery.data]);

	const saveMutation = useMutation({
		mutationKey: ["adminSaveApiSettings"],
		mutationFn: (data: ApiSettings) => authenticateAndPostData("/admin/api-settings/", data),
		onSuccess: () => enqueueSnackbar("API settings saved.", {variant: "success"}),
		onError: (error: ApiRequestFailed) => enqueueSnackbar(error.data.message, {variant: "error"}),
	});

	if (!settings) {
		return null;
	}

	const numberField = (key: keyof ApiSettings, label: string, helperText: string, step = 1) => (
		<TextField
			size="small"
			type="number"
			label={label}
			helperText={helperText}
			value={settings[key] as number}
			inputProps={{step}}
			onChange={(e) => setSettings({...settings, [key]: Number(e.target.value)})}
		/>
	);

	return (
		<Card variant="outlined" sx={{mb: 2}}>
			<CardContent>
				<Typography variant="h6" gutterBottom>API Sending Safety</Typography>
				<Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems="flex-start">
					{numberField("api_send_interval_seconds", "Send interval (seconds)",
						"Minimum gap between API emails from one domain. Faster sends are queued.")}
					{numberField("api_max_scheduled_per_domain", "Queue size per domain",
						"Queued emails a domain can hold before sends get 429.")}
					{numberField("api_abuse_alert_threshold", "Alert threshold",
						"Suspicious emails since last review before admins are emailed.")}
					{numberField("api_abuse_confidence_threshold", "Classifier confidence",
						"Minimum classifier confidence (0-1) to flag an email.", 0.05)}
				</Stack>
				<Typography variant="subtitle1" sx={{mt: 2, mb: 1}}>Bounce limit per domain</Typography>
				<Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems="flex-start">
					{numberField("api_bounce_limit_count", "Bounces allowed",
						"Bounces within the window that pause a domain's API sending. 0 turns the limit off.")}
					{numberField("api_bounce_window_minutes", "Window (minutes)",
						"Time window the bounces are counted in.")}
					{numberField("api_bounce_pause_minutes", "Pause (minutes)",
						"How long the domain stays paused. 0 keeps it paused until lifted below.")}
				</Stack>
				<Stack direction="row" spacing={2} alignItems="flex-end" sx={{mt: 3}}>
					<Stack spacing={1}>
						<FormControlLabel
							control={<Switch checked={settings.enable_api_bounce_pause_email}
											 onChange={(e) => setSettings({...settings, enable_api_bounce_pause_email: e.target.checked})}/>}
							label="Email the account owner when a domain is paused"
						/>
						<FormControlLabel
							control={<Switch checked={settings.enable_api_abuse_alert_email}
											 onChange={(e) => setSettings({...settings, enable_api_abuse_alert_email: e.target.checked})}/>}
							label="Email admins when an account crosses the threshold"
						/>
					</Stack>
					<Box sx={{flexGrow: 1}}/>
					<Button variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate(settings)}>
						Save
					</Button>
				</Stack>
			</CardContent>
		</Card>
	);
}

function BouncePausesCard() {
	const {enqueueSnackbar} = useSnackbar();

	const pausesQuery = useQuery({
		queryKey: ["adminApiBouncePauses"],
		queryFn: () => authenticateAndFetchData("/admin/api-bounce-pauses/"),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	const pauses: BouncePause[] = pausesQuery.data?.data?.pauses ?? [];

	const clearMutation = useMutation({
		mutationKey: ["adminClearApiBouncePause"],
		mutationFn: (managedSubdomainId: number) => authenticateAndPostData("/admin/api-bounce-pauses/clear/", {
			managed_subdomain_id: managedSubdomainId,
		}),
		onSuccess: () => {
			enqueueSnackbar("Bounce pause lifted.", {variant: "success"});
			pausesQuery.refetch();
		},
		onError: (error: ApiRequestFailed) => enqueueSnackbar(error.data.message, {variant: "error"}),
	});

	return (
		<Card variant="outlined" sx={{mb: 2}}>
			<CardContent>
				<Typography variant="h6" gutterBottom>Domains Paused For Bounces</Typography>
				{pauses.length === 0 ? (
					<Typography variant="body2" color="text.secondary">No domains are paused right now.</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Domain</TableCell>
								<TableCell>Account</TableCell>
								<TableCell>Workspace</TableCell>
								<TableCell>Bounces</TableCell>
								<TableCell>Paused On</TableCell>
								<TableCell>Paused Until</TableCell>
								<TableCell/>
							</TableRow>
						</TableHead>
						<TableBody>
							{pauses.map((pause) => (
								<TableRow key={pause.managed_subdomain_id}>
									<TableCell>{pause.domain}</TableCell>
									<TableCell>{pause.user_email}</TableCell>
									<TableCell>{pause.workspace_name}</TableCell>
									<TableCell>{pause.bounce_count} in {pause.window_minutes} min</TableCell>
									<TableCell>{formatDate(pause.paused_on)}</TableCell>
									<TableCell>{pause.paused_until ? formatDate(pause.paused_until) : "Until lifted"}</TableCell>
									<TableCell align="right">
										<Button size="small" disabled={clearMutation.isPending}
												onClick={() => clearMutation.mutate(pause.managed_subdomain_id)}>
											Lift pause
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}

function AccountReviewDialog(props: { account: FlaggedAccount, onClose: () => void, onChanged: () => void }) {
	const {enqueueSnackbar} = useSnackbar();
	const [notes, setNotes] = useState(props.account.notes);
	const [pendingAction, setPendingAction] = useState<typeof ACTIONS[number] | null>(null);
	const [openSignal, setOpenSignal] = useState<AbuseSignal | null>(null);

	const signalsQuery = useQuery({
		queryKey: ["adminApiAbuseSignals", props.account.user_id],
		queryFn: () => authenticateAndFetchData(`/admin/api-abuse/signals/?user_id=${props.account.user_id}&page_from=0&page_to=100`),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});
	const signals: AbuseSignal[] = signalsQuery.data?.data?.signals ?? [];

	const actionMutation = useMutation({
		mutationKey: ["adminApiAbuseAction"],
		mutationFn: (action: ReviewAction) => authenticateAndPostData("/admin/api-abuse/action/", {
			user_id: props.account.user_id,
			action,
			notes,
		}),
		onSuccess: (response) => {
			enqueueSnackbar(`Account is now: ${STATUS_LABELS[response.data.status as ReviewStatus]}.`, {variant: "success"});
			setPendingAction(null);
			props.onChanged();
			props.onClose();
		},
		onError: (error: ApiRequestFailed) => enqueueSnackbar(error.data.message, {variant: "error"}),
	});

	return (
		<Dialog open onClose={props.onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				{props.account.user_email}{" "}
				<Chip size="small" color={STATUS_COLORS[props.account.status]} label={STATUS_LABELS[props.account.status]}/>
			</DialogTitle>
			<DialogContent dividers>
				<Stack direction="row" spacing={3} sx={{mb: 2}} flexWrap="wrap">
					<Typography variant="body2"><b>Suspicious emails:</b> {props.account.suspicious_count} ({props.account.flags_since_review} since last review)</Typography>
					<Typography variant="body2"><b>Domains:</b> {props.account.domains.join(", ") || "-"}</Typography>
					<Typography variant="body2"><b>Workspaces:</b> {props.account.workspaces.join(", ") || "-"}</Typography>
					<Typography variant="body2"><b>Rate limit hits (7d):</b> {props.account.rate_limit_hits_7d}</Typography>
					<Typography variant="body2"><b>Last reviewed:</b> {props.account.reviewed_by ? `${props.account.reviewed_by}, ${formatDate(props.account.reviewed_on)}` : "Never"}</Typography>
				</Stack>

				<Typography variant="subtitle2" gutterBottom>Flagged emails</Typography>
				{signalsQuery.isLoading && <Typography variant="body2">Loading...</Typography>}
				{signals.map((signal) => (
					<Box key={signal.id} sx={{py: 1}}>
						<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
							<Chip size="small" color="error" label={`${signal.category} (${Math.round(signal.confidence * 100)}%)`}/>
							<Typography variant="body2"><b>{signal.subject}</b></Typography>
							<Typography variant="body2" color="text.secondary">
								{signal.from} → {signal.to} · {signal.workspace_name} · {formatDate(signal.flagged_on)}
							</Typography>
							<Button size="small" onClick={() => setOpenSignal(signal)}>View email</Button>
						</Stack>
						<Typography variant="caption" color="text.secondary">{signal.reason}</Typography>
						<Divider sx={{mt: 1}}/>
					</Box>
				))}

				<TextField
					label="Review notes"
					multiline
					minRows={2}
					fullWidth
					sx={{mt: 2}}
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
				/>
			</DialogContent>
			<DialogActions sx={{flexWrap: "wrap", gap: 1}}>
				{ACTIONS.map((item) => (
					<Button key={item.action} color={item.color} variant="outlined" onClick={() => setPendingAction(item)}>
						{item.label}
					</Button>
				))}
				<Box sx={{flexGrow: 1}}/>
				<Button onClick={props.onClose}>Close</Button>
			</DialogActions>

			<Dialog open={pendingAction !== null} onClose={() => setPendingAction(null)}>
				<DialogTitle>{pendingAction?.label}?</DialogTitle>
				<DialogContent>
					<DialogContentText>{pendingAction?.confirm}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setPendingAction(null)}>Cancel</Button>
					<Button color={pendingAction?.color} variant="contained" disabled={actionMutation.isPending}
							onClick={() => pendingAction && actionMutation.mutate(pendingAction.action)}>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={openSignal !== null} onClose={() => setOpenSignal(null)} maxWidth="md" fullWidth>
				<DialogTitle>{openSignal?.subject}</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						{openSignal?.from} → {openSignal?.to} · Email ID {openSignal?.email_id}
					</Typography>
					{/* Shown as text, never rendered: this is the suspicious content being reviewed. */}
					<pre style={{whiteSpace: "pre-wrap", wordWrap: "break-word"}}>
						{openSignal?.plain_body ?? openSignal?.html_body}
					</pre>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenSignal(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
}

export default function AdminApiAbuse() {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState<ReviewStatus | "">("");
	const [selectedAccount, setSelectedAccount] = useState<FlaggedAccount | null>(null);

	const from = (page - 1) * pageSize;
	const accountsQuery = useQuery({
		queryKey: ["adminApiAbuseAccounts", page, pageSize, search, status],
		queryFn: () => authenticateAndFetchData(
			`/admin/api-abuse/accounts/?page_from=${from}&page_to=${from + pageSize}` +
			`&search=${encodeURIComponent(search)}${status ? `&status=${status}` : ""}`
		),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	});

	const totalCount: number = accountsQuery.data?.data?.total_count ?? 0;
	const paginationData = {
		data: (accountsQuery.data?.data?.accounts ?? []) as FlaggedAccount[],
		total_count: totalCount,
		total_pages: Math.max(Math.ceil(totalCount / pageSize), 1),
		current_page: page,
		page_size: pageSize,
	};

	const StatusCell = (params: CustomCellRendererProps) => (
		<Chip size="small" color={STATUS_COLORS[params.value as ReviewStatus]} label={STATUS_LABELS[params.value as ReviewStatus]}/>
	);

	const ReviewButton = (params: CustomCellRendererProps) => (
		<Button size="small" onClick={() => setSelectedAccount(params.data as FlaggedAccount)}>Review</Button>
	);

	const columnDefs: ColDef[] = [
		{field: "user_email", headerName: "Account", flex: 1.5},
		{field: "status", headerName: "Status", cellRenderer: StatusCell},
		{field: "flags_since_review", headerName: "New Flags"},
		{field: "suspicious_count", headerName: "Total Flags"},
		{
			field: "categories", headerName: "Categories", flex: 1.5, sortable: false,
			valueFormatter: (params) => Object.entries(params.value as Record<string, number>)
				.map(([category, count]) => `${category} (${count})`).join(", "),
		},
		{
			field: "domains", headerName: "Domains", flex: 1.5, sortable: false,
			valueFormatter: (params) => (params.value as string[]).join(", "),
		},
		{field: "rate_limit_hits_7d", headerName: "Rate Limit Hits (7d)"},
		{field: "last_flagged_on", headerName: "Last Flagged", valueFormatter: (params) => formatDate(params.value)},
		{headerName: "", cellRenderer: ReviewButton, sortable: false, resizable: false},
	];

	if (accountsQuery.error) {
		return <PageDataErrorHandler error={accountsQuery.error as unknown as ApiRequestFailed}/>;
	}

	return (
		<Box sx={{height: "100%", width: "100%", overflow: "hidden"}}>
			<Typography variant="h5" gutterBottom>Fishy Accounts / API Abuse</Typography>
			<Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
				Accounts whose API emails the content classifier flagged. Nothing is blocked automatically: review the
				flagged emails and decide what to do.
			</Typography>

			<ApiSettingsCard/>
			<BouncePausesCard/>

			<PaginatedServerSideDataTable
				columns={columnDefs}
				paginationData={paginationData}
				loading={accountsQuery.isLoading || accountsQuery.isFetching}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				onSearchChange={(value) => {
					setSearch(value);
					setPage(1);
				}}
				searchPlaceholder="Search by account email..."
				noRowsText="No flagged accounts."
				actions={
					<FormControl size="small" sx={{minWidth: 200}}>
						<InputLabel>Status</InputLabel>
						<Select label="Status" value={status} onChange={(e) => {
							setStatus(e.target.value as ReviewStatus | "");
							setPage(1);
						}}>
							<MenuItem value="">All</MenuItem>
							{(Object.keys(STATUS_LABELS) as ReviewStatus[]).map((key) => (
								<MenuItem key={key} value={key}>{STATUS_LABELS[key]}</MenuItem>
							))}
						</Select>
					</FormControl>
				}
			/>

			{selectedAccount && (
				<AccountReviewDialog
					account={selectedAccount}
					onClose={() => setSelectedAccount(null)}
					onChanged={() => accountsQuery.refetch()}
				/>
			)}
		</Box>
	);
}
